import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  CreditCard as CardIcon,
  QrCode as QrIcon,
  AttachMoney as CashIcon,
} from '@mui/icons-material';
import { useSalesModal } from '../../../hooks/useSalesModal';
import { useOrdersPermissions } from '../../../hooks/useOrdersPermissions';
import { salesService } from '../../../services/sales.service';
import { SalesOrder, CreateSalesOrderItemDto } from '../../../api';
import { selectCurrentUser } from '../../../redux/slices/auth.slice';
import { roundMoney } from '../../../utils/money';
import { buildCreateOrderPayload } from '../../../utils/sales/build-create-order-payload';
import {
  cartHasRemoteItems,
  countRemoteFulfillmentGroups,
} from '../../../utils/sales/mixed-sale.utils';
import { extractErrorMessage } from '../../../utils/errorHelpers';

// Opciones de métodos de pago para UI
const PAYMENT_METHOD_OPTIONS = [
  { id: 'CASH', name: 'Efectivo', icon: CashIcon, color: 'success.main' },
  { id: 'CARD', name: 'Tarjeta', icon: CardIcon, color: 'primary.main' },
  { id: 'QR', name: 'QR', icon: QrIcon, color: 'secondary.main' },
];

export interface SalesOrderCompletedPayload {
  orderCode: string;
  branchReceiptCode?: string | null;
  /** ISO 8601: fecha comercial de la venta (`saleDate` en servidor; puede diferir del momento de registro). */
  completedAt: string;
}

export interface UseSalesStep3Props {
  onOrderCompleted?: (payload: SalesOrderCompletedPayload) => void;
}

export const useSalesStep3 = ({ onOrderCompleted }: UseSalesStep3Props) => {
  const {
    state,
    orderSummary,
    setLoading,
    previousStep,
    finish,
    chargeCategories,
    setProductDelivered,
    applyDeliveryDefaults,
  } = useSalesModal();
  const currentUser = useSelector(selectCurrentUser);
  const { canMixedSell } = useOrdersPermissions();

  const [isProcessing, setIsProcessing] = useState(false);
  const processingRef = useRef(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [branchReceiptNumber, setBranchReceiptNumber] = useState<string | null>(null);
  const [orderCompletedAt, setOrderCompletedAt] = useState<string | null>(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [, setCreatedOrder] = useState<SalesOrder | null>(null);
  const defaultsAppliedRef = useRef(false);

  const sellingLocationId = state.selectedLocation?.id;

  // Apply delivery defaults once when entering confirmation (payment already known).
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    defaultsAppliedRef.current = true;
    applyDeliveryDefaults(orderSummary.paymentStatus, sellingLocationId);
  }, [applyDeliveryDefaults, orderSummary.paymentStatus, sellingLocationId]);

  const isMixedSale = useMemo(
    () => cartHasRemoteItems(state.selectedProducts, sellingLocationId),
    [state.selectedProducts, sellingLocationId],
  );

  const remoteGroupCount = useMemo(
    () => countRemoteFulfillmentGroups(state.selectedProducts, sellingLocationId),
    [state.selectedProducts, sellingLocationId],
  );

  const canConfirmMixedSale = !isMixedSale || canMixedSell;

  // Detect products with loss
  const lossItems = useMemo(
    () =>
      state.selectedProducts
        .map((product) => {
          const cost = roundMoney(product.warehouseInventoryProduct.costPrice || 0);
          const final = roundMoney(product.finalPrice);
          const loss = roundMoney(cost - final);
          return {
            code: product.warehouseInventoryProduct.productCodeUnique,
            name: product.warehouseInventoryProduct.product.name,
            loss,
          };
        })
        .filter((p) => p.loss > 0),
    [state.selectedProducts],
  );

  const getDiscountType = useCallback(
    (discountType: string): CreateSalesOrderItemDto.discountType => {
      switch (discountType) {
        case 'PERCENTAGE':
          return CreateSalesOrderItemDto.discountType.PERCENTAGE;
        case 'AMOUNT':
          return CreateSalesOrderItemDto.discountType.AMOUNT;
        default:
          return CreateSalesOrderItemDto.discountType.NONE;
      }
    },
    [],
  );

  const getMarkupType = useCallback((markupType: string): CreateSalesOrderItemDto.markupType => {
    switch (markupType) {
      case 'PERCENTAGE':
        return CreateSalesOrderItemDto.markupType.PERCENTAGE;
      case 'AMOUNT':
        return CreateSalesOrderItemDto.markupType.AMOUNT;
      default:
        return CreateSalesOrderItemDto.markupType.NONE;
    }
  }, []);

  const openConfirmModal = useCallback(() => {
    if (!canConfirmMixedSale) {
      setConfirmError(
        'No tiene permiso para confirmar ventas con piezas de otras sucursales o almacén (orders.sales-management.mixed_sell).',
      );
      return;
    }
    setConfirmError(null);
    setShowConfirmModal(true);
  }, [canConfirmMixedSale]);

  const closeConfirmModal = useCallback(() => {
    if (isProcessing) return;
    setShowConfirmModal(false);
  }, [isProcessing]);

  // Handle confirm sale
  const handleConfirmSale = useCallback(async () => {
    if (processingRef.current || isProcessing) {
      return;
    }

    if (!canConfirmMixedSale) {
      setConfirmError(
        'No tiene permiso para confirmar ventas con piezas de otras sucursales o almacén (orders.sales-management.mixed_sell).',
      );
      setShowConfirmModal(false);
      return;
    }

    processingRef.current = true;
    setConfirmError(null);
    setIsProcessing(true);
    setLoading(true);

    try {
      const orderData = buildCreateOrderPayload({
        state,
        orderSummary,
        currentUserId: currentUser?.id,
        getDiscountType,
        getMarkupType,
      });

      const newOrder = isMixedSale
        ? await salesService.createMixedSale(orderData)
        : await salesService.createSalesOrder(orderData);

      setCreatedOrder(newOrder as any);
      setOrderNumber(newOrder.orderCode);
      setBranchReceiptNumber(newOrder.branchReceiptCode ?? null);
      const completedAtIso = newOrder.saleDate.toISOString();
      setOrderCompletedAt(completedAtIso);
      setShowConfirmModal(false);

      if (onOrderCompleted) {
        onOrderCompleted({
          orderCode: newOrder.orderCode,
          branchReceiptCode: newOrder.branchReceiptCode ?? null,
          completedAt: completedAtIso,
        });
      }
    } catch (error) {
      console.error('Error al procesar la venta:', error);
      setShowConfirmModal(false);

      setConfirmError(
        extractErrorMessage(error, 'No se pudo confirmar la venta. Intente de nuevo.'),
      );
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
      setLoading(false);
    }
  }, [
    canConfirmMixedSale,
    isProcessing,
    state,
    orderSummary,
    currentUser,
    onOrderCompleted,
    setLoading,
    getDiscountType,
    getMarkupType,
    isMixedSale,
  ]);

  // Handle print receipt
  const handlePrintReceipt = useCallback(() => {
    setShowPrintModal(true);
  }, []);

  // Handle finish
  const handleFinish = useCallback(() => {
    setIsProcessing(false);
    setLoading(false);
    finish();
  }, [finish, setLoading]);

  // Handle close print modal
  const handleClosePrintModal = useCallback(() => {
    setShowPrintModal(false);
    handleFinish();
  }, [handleFinish]);

  return {
    // State
    state,
    orderSummary,
    isProcessing,
    orderNumber,
    branchReceiptNumber,
    orderCompletedAt,
    showPrintModal,
    showConfirmModal,
    lossItems,
    paymentMethods: PAYMENT_METHOD_OPTIONS,
    chargeCategories,
    isMixedSale,
    remoteGroupCount,
    canConfirmMixedSale,
    confirmError,
    sellingLocationId,
    // Handlers
    openConfirmModal,
    closeConfirmModal,
    handleConfirmSale,
    handlePrintReceipt,
    handleFinish,
    handleClosePrintModal,
    previousStep,
    setProductDelivered,
  };
};
